import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { cn, vaultSpring } from '../lib/utils';
import { useViewport } from '../hooks';

export interface GalleryImage {
  src: string;
  alt: string;
}

export interface PropertyGalleryProps {
  images: GalleryImage[];
  className?: string;
}

export const PropertyGallery: React.FC<PropertyGalleryProps> = ({
  images,
  className,
}) => {
  const { isMobile } = useViewport();
  const [activeIndex, setActiveIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const goToImage = (index: number) => {
    setActiveIndex(index);
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showLightbox) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') setLightboxIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      if (e.key === 'ArrowRight') setLightboxIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox, images.length]);

  // Mobile: Horizontal scroll gallery
  if (isMobile) {
    return (
      <>
        <div className={cn('relative', className)}>
          <div
            ref={scrollRef}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {images.map((image, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-full snap-center pr-4 last:pr-0"
              >
                <div
                  className="relative aspect-[4/3] rounded-vault-lg overflow-hidden"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <div className="absolute bottom-3 right-3 p-2 rounded-full bg-black/50 backdrop-blur-sm">
                    <ZoomIn className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Dots indicator */}
          <div className="flex justify-center gap-2 mt-4">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                className={cn(
                  'w-2 h-2 rounded-full transition-colors',
                  index === activeIndex
                    ? 'bg-gold-500'
                    : 'bg-white/20'
                )}
              />
            ))}
          </div>
        </div>

        {/* Lightbox */}
        <AnimatePresence>
          {showLightbox && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
            >
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 p-3 rounded-full bg-white/10 backdrop-blur-sm z-10"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              
              <div className="w-full h-full flex items-center justify-center p-4">
                <img
                  src={images[lightboxIndex].src}
                  alt={images[lightboxIndex].alt}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              
              {/* Navigation */}
              <button
                onClick={() => setLightboxIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                className="absolute left-4 p-3 rounded-full bg-white/10 backdrop-blur-sm"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={() => setLightboxIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
                className="absolute right-4 p-3 rounded-full bg-white/10 backdrop-blur-sm"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
              
              {/* Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm">
                <span className="text-sm text-white tabular-nums">
                  {lightboxIndex + 1} / {images.length}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  // Desktop: Grid gallery with featured image
  return (
    <>
      <div className={cn('grid grid-cols-4 grid-rows-2 gap-3 h-[500px]', className)}>
        {/* Main featured image */}
        <motion.div
          className="col-span-2 row-span-2 relative rounded-vault-lg overflow-hidden cursor-pointer group"
          onClick={() => openLightbox(0)}
          whileHover={{ scale: 1.01 }}
          transition={vaultSpring}
        >
          <img
            src={images[0]?.src}
            alt={images[0]?.alt}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="absolute bottom-4 right-4 p-2 rounded-full bg-black/50 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn className="w-5 h-5 text-white" />
          </div>
        </motion.div>

        {/* Secondary images */}
        {images.slice(1, 5).map((image, index) => (
          <motion.div
            key={index + 1}
            className="relative rounded-vault-lg overflow-hidden cursor-pointer group"
            onClick={() => openLightbox(index + 1)}
            whileHover={{ scale: 1.02 }}
            transition={vaultSpring}
          >
            <img
              src={image.src}
              alt={image.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            
            {/* Show more overlay on last visible image */}
            {index === 3 && images.length > 5 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                <span className="text-white font-display text-xl font-semibold">
                  +{images.length - 5} more
                </span>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Desktop Lightbox */}
      <AnimatePresence>
        {showLightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-lg flex items-center justify-center"
            onClick={closeLightbox}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={vaultSpring}
              className="relative max-w-6xl max-h-[90vh] w-full mx-8"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeLightbox}
                className="absolute -top-12 right-0 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              
              <img
                src={images[lightboxIndex].src}
                alt={images[lightboxIndex].alt}
                className="w-full h-full max-h-[80vh] object-contain rounded-vault-lg"
              />
              
              {/* Navigation */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
              
              {/* Thumbnails */}
              <div className="flex justify-center gap-2 mt-4 overflow-x-auto py-2">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setLightboxIndex(index)}
                    className={cn(
                      'flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-colors',
                      index === lightboxIndex
                        ? 'border-gold-500'
                        : 'border-transparent opacity-60 hover:opacity-100'
                    )}
                  >
                    <img
                      src={image.src}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
